import React, {useCallback, useContext, useEffect, useRef, useState} from "react"

import http from "./../services/httpService";
import httpNoAuth from "./../services/httpNoAuthService";
import config from "../constants";
import {navigation} from "../routing/main_navigation";
import {getAccountInfo} from "../api/accounts";

const apiEndpoint = config.API_URL + "/accounts/";


const AuthContext = React.createContext()

export function useAuth() {
    return useContext(AuthContext)
}

export function AuthProvider({children}) {

    const [currentUser, setCurrentUser] = useState()
    const [loading, setLoading] = useState(true)
    const [permissions, setPermissions] = useState([])
    // const [navigation, setNavigation] = useState([])
    const [currentMainNavItem, setCurrentMainNavItem] = useState()
    const [userInfo, setUserInfo] = useState({})
    const isLoggingOutRef = useRef(false)

    // Helper function to get CSRF token from cookies
    const getCsrfToken = useCallback(() => {
        const name = 'csrftoken';
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    }, [])

    const ensureCsrfToken = useCallback(async () => {
        try {
            // IMPORTANT: Always fetch a fresh CSRF token before login
            // This is critical because:
            // 1. CSRF tokens can expire after a few minutes of inactivity
            // 2. Stale tokens will cause 403 errors
            // 3. We need a fresh token for each login attempt

            // Fetch a fresh CSRF token - the backend will set a new cookie
            const csrfResponse = await httpNoAuth.get(apiEndpoint + "csrf/")

            // Try to get token from response body first (for cross-domain scenarios)
            let token = null
            if (csrfResponse.data && csrfResponse.data.csrfToken) {
                token = csrfResponse.data.csrfToken
            } else if (csrfResponse.data && typeof csrfResponse.data === 'string') {
                // Sometimes the token is returned as a plain string
                token = csrfResponse.data
            }

            // If not in response body, try to get from cookie
            if (!token) {
                // Wait a tiny bit for the cookie to be set
                await new Promise(resolve => setTimeout(resolve, 100))
                token = getCsrfToken()
            }

            if (!token) {
                throw new Error('CSRF token was not set by the server')
            }

            return token
        } catch (e) {
            throw e // Re-throw so login knows it failed
        }
    }, [getCsrfToken])

    const loadCurrentUser = useCallback(async () => {
        try {
            setLoading(true)
            const response = await http.get("/accounts/account_info/")
            setCurrentUser(response.data)
            setUserInfo(response.data)
        } catch (e) {
            setCurrentUser(undefined)
            setUserInfo({})
            if (e?.response?.status === 401 && window.location.pathname !== '/login') {
                window.location = '/login'
            }
        } finally {
            setLoading(false)
        }
    }, [])

    async function login(email, password) {
        try {
            // Get a fresh CSRF token from a public endpoint before login
            const csrfToken = await ensureCsrfToken()

            // Make the login request with explicit CSRF token header
            const response = await httpNoAuth.post(
                apiEndpoint + "session-login/",
                {"username": email, "password": password},
                {
                    headers: {
                        'X-CSRFToken': csrfToken
                    }
                }
            );

            // Store the CSRF token in sessionStorage for use by authenticated requests
            // This is needed for cross-domain scenarios where cookies can't be read
            if (csrfToken) {
                sessionStorage.setItem('csrfToken', csrfToken);
            }

            await loadCurrentUser()
        } catch (error) {
            throw error
        }
    }

    const logout = useCallback(async () => {
        // Prevent multiple simultaneous logout attempts using ref
        if (isLoggingOutRef.current) {
            return
        }

        isLoggingOutRef.current = true

        // Clear user state immediately to prevent any further API calls
        setCurrentUser(undefined)
        setUserInfo({})
        setLoading(true)

        try {
            await http.post(apiEndpoint + "session-logout/")
        } catch (e) {
            // Ignore errors - user is being logged out anyway
        } finally {
            setLoading(false)
        }

        // Clear CSRF token from sessionStorage
        sessionStorage.removeItem('csrfToken');

        // Redirect to login page immediately
        window.location.href = '/login'
    }, [])

    const loginAs = async (userId) => {
        await http.post(config.API_URL + `/users_for_admin/${userId}/session-login/`)
        await loadCurrentUser()
    }

    useEffect(() => {
        // IMPORTANT: We cannot check for sessionid cookie in JavaScript because it's HttpOnly
        // Instead, we always try to load the user and let the API call determine if we're authenticated

        // Always try to load the user - the backend will return 401 if not authenticated
        loadCurrentUser()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!currentMainNavItem) {
            const path = window.location.pathname

            // First check if current path matches any child path
            let navItem = navigation.find((item) => {
                if (item.children && item.children.length > 0) {
                    return item.children.some(child => path.startsWith(child.path))
                }
                return false
            })

            // If no match in children, try to find exact match in main nav
            if (!navItem) {
                navItem = navigation.find((item) => item.path === path)
            }

            setCurrentMainNavItem(navItem)
        }
    }, [])

    const loadUserInfo = useCallback(async () => {
        // Reload user info - this will make a fresh API call
        const d = await getAccountInfo()
        setUserInfo(d)
        setCurrentUser(d)
    }, [])

    // Note: userInfo is now loaded together with currentUser in loadCurrentUser()
    // This useEffect is no longer needed for initial load, only for manual refreshes

    const value = {
        currentUser,
        loading,
        permissions,
        navigation,
        setCurrentMainNavItem,
        currentMainNavItem,
        userInfo,
        loadUserInfo,
        loadCurrentUser,
        login,
        logout,
        loginAs
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}
